import React from 'react';
import type { RouteRecord } from 'vite-react-ssg';
import App from './App';
import Home from './pages/Home';
import { PACKAGE_DETAILS } from './data/tourPackagesData';
import { SANITY_PACKAGE_SLUGS } from './data/sanityPackageSlugs';
import { UAE_TOUR_ACTIVITIES } from './data/uaeToursData';
import { CORPORATE_INDUSTRIES } from './data/corporateIndustries';
import { BLOG_POSTS } from './data/blogPosts';
import { slugify } from './hooks/useUaeTours';
import {
  fetchTourPackageDetailLoaderData,
  fetchTourPackagesFromSanity,
  fetchTourPackageStaticPaths,
} from './lib/sanityTourPackages';

// -- Dynamic route seed data ---------------------------------------------------
const DEST_SLUGS = [
  'dubai', 'maldives', 'bali', 'turkey', 'georgia', 'europe',
  'saudi-arabia', 'switzerland', 'paris', 'italy', 'spain',
  'united-kingdom', 'thailand', 'malaysia', 'japan', 'singapore',
  'abu-dhabi', 'sharjah', 'ras-al-khaimah', 'kenya', 'morocco',
  'egypt', 'mauritius', 'seychelles', 'oman', 'jordan',
];

const SERVICE_SLUGS = [
  'visa', 'flights', 'umrah', 'hotels', 'desert-safari',
  'dhow-cruise', 'corporate-travel', 'travel-insurance',
  'uae-tours', 'cruise',
];

// -- Route tree ----------------------------------------------------------------
export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <App />,
    children: [
      // -- Core pages ------------------------------------------------------
      {
        index: true,
        element: <Home />,
        // No SSG loader — avoids the React Router Suspense boundary that
        // renderToPipeableStream emits for data-loader routes, which caused
        // a $RC template-swap flash on every page load. Sanity packages are
        // fetched client-side by useTourPackages() with a static fallback.
      },
      { path: 'about',        Component: React.lazy(() => import('./pages/About')) },
      {
        path: 'tour-packages',
        Component: React.lazy(() => import('./pages/TourPackages')),
        loader: async () => ({ packages: await fetchTourPackagesFromSanity() }),
      },
      {
        path: 'tour-packages/:slug',
        lazy: async () => { const { default: Component } = await import('./pages/TourDetail'); return { Component }; },
        loader: async ({ params }) => fetchTourPackageDetailLoaderData(params.slug),
        getStaticPaths: () => fetchTourPackageStaticPaths([
          ...PACKAGE_DETAILS.map(p => p.id),
          ...SANITY_PACKAGE_SLUGS,
        ]),
      },
      { path: 'flights',   Component: React.lazy(() => import('./pages/AirlineTicketing')) },
      { path: 'visas',     Component: React.lazy(() => import('./pages/VisitVisas')) },
      { path: 'hotels',    Component: React.lazy(() => import('./pages/HotelReservations')) },
      { path: 'uae-tours', Component: React.lazy(() => import('./pages/UaeTours')) },
      { path: 'uae-tours-preview', Component: React.lazy(() => import('./pages/UaeToursPreview')) },
      { path: 'uae-tours/desert-safari', Component: React.lazy(() => import('./pages/DesertSafari')) },
      {
        path: 'uae-tours/:slug',
        lazy: async () => { const { default: Component } = await import('./pages/UaeTourDetail'); return { Component }; },
        // Prerender each UAE tour detail page (e.g. /uae-tours/dhow-cruise-dinner/)
        // so they are real, indexable pages. 'desert-safari' has its own
        // dedicated route/page above, so exclude it here.
        getStaticPaths: () =>
          UAE_TOUR_ACTIVITIES.map(t => slugify(t.name))
            .filter(s => s !== 'desert-safari')
            .map(s => `uae-tours/${s}`),
      },
      { path: 'umrah',     Component: React.lazy(() => import('./pages/Umrah')) },
      { path: 'corporate-travel', Component: React.lazy(() => import('./pages/CorporateTravel')) },
      { path: 'corporate-travel/construction-companies', Component: React.lazy(() => import('./pages/corporate/ConstructionTravel')) },
      { path: 'corporate-travel/oil-and-gas-companies', Component: React.lazy(() => import('./pages/corporate/OilGasTravel')) },
      {
        // Data-driven corporate-travel industry sub-pages (events/MICE,
        // hospitality & retail, consulting/finance/tech, government). The two
        // bespoke pages above use static segments, so they always match first.
        path: 'corporate-travel/:industry',
        lazy: async () => { const { default: Component } = await import('./pages/corporate/IndustryTravel'); return { Component }; },
        getStaticPaths: () => CORPORATE_INDUSTRIES.map(i => `corporate-travel/${i.slug}`),
      },
      { path: 'cruise',    Component: React.lazy(() => import('./pages/Cruise')) },
      { path: 'insurance', Component: React.lazy(() => import('./pages/Insurance')) },
      { path: 'blog',      Component: React.lazy(() => import('./pages/Blog')) },
      {
        // Prerender every blog post to static, indexable HTML.
        path: 'blog/:slug',
        lazy: async () => { const { default: Component } = await import('./pages/BlogPost'); return { Component }; },
        getStaticPaths: () => BLOG_POSTS.map(p => `blog/${p.slug}`),
      },
      { path: 'contact',   Component: React.lazy(() => import('./pages/Contact')) },

      // -- Visa sub-pages --------------------------------------------------
      { path: 'visas/uae-visit-visa',  Component: React.lazy(() => import('./pages/visas/UaeVisitVisa')) },
      { path: 'visas/schengen-visa',   Component: React.lazy(() => import('./pages/visas/SchengenVisa')) },
      { path: 'visas/uk-visa',         Component: React.lazy(() => import('./pages/visas/UkVisa')) },
      { path: 'visas/usa-visa',        Component: React.lazy(() => import('./pages/visas/UsaVisa')) },
      { path: 'visas/canada-visa',     Component: React.lazy(() => import('./pages/visas/CanadaVisa')) },
      { path: 'visas/australia-visa',  Component: React.lazy(() => import('./pages/visas/AustraliaVisa')) },
      { path: 'visas/azerbaijan-visa', Component: React.lazy(() => import('./pages/visas/AzerbaijanVisa')) },
      { path: 'visas/malaysia-visa',   Component: React.lazy(() => import('./pages/visas/MalaysiaVisa')) },
      { path: 'visas/saudi-visa',      Component: React.lazy(() => import('./pages/visas/SaudiVisa')) },
      { path: 'visas/golden-visa',     Component: React.lazy(() => import('./pages/visas/GoldenVisa')) },
      { path: 'visas/visa-extension',  Component: React.lazy(() => import('./pages/visas/VisaExtension')) },
      { path: 'visas/family-visa',     Component: React.lazy(() => import('./pages/visas/FamilyVisa')) },
      { path: 'visas/job-seeker-visa', Component: React.lazy(() => import('./pages/visas/JobSeekerVisa')) },
      { path: 'visas/transit-visa',    Component: React.lazy(() => import('./pages/visas/TransitVisa')) },
      { path: 'visas/freelance-visa',  Component: React.lazy(() => import('./pages/visas/FreelanceVisa')) },

      // -- Dynamic: destination pages --------------------------------------
      {
        path: 'destinations/:slug',
        lazy: async () => { const { default: Component } = await import('./pages/DestinationPage'); return { Component }; },
        getStaticPaths: () => DEST_SLUGS.map(s => `destinations/${s}`),
      },

      // -- Dynamic: service pages ------------------------------------------
      {
        path: 'services/:slug',
        lazy: async () => { const { default: Component } = await import('./pages/ServicePage'); return { Component }; },
        getStaticPaths: () => SERVICE_SLUGS.map(s => `services/${s}`),
      },

      // -- Policy pages ----------------------------------------------------
      { path: 'privacy-policy', Component: React.lazy(() => import('./pages/policies/PrivacyPolicy')) },
      { path: 'terms',          Component: React.lazy(() => import('./pages/policies/Terms')) },
      { path: 'refund-policy',  Component: React.lazy(() => import('./pages/policies/RefundPolicy')) },
      { path: 'cookie-policy',  Component: React.lazy(() => import('./pages/policies/CookiePolicy')) },

      // -- 404 catch-all ---------------------------------------------------
      { path: '*', Component: React.lazy(() => import('./pages/NotFound')) },
    ],
  },

  // -- Sanity Studio - top-level, no Navbar/Footer, skipped by SSG (wildcard) --
  {
    path: 'studio/*',
    lazy: async () => {
      const { default: Component } = await import('./pages/Studio');
      return { Component };
    },
  },
];
